import { DomHelpers } from '../core/DomHelpers.js';
import { Widget } from '../core/Widget.js';
import { keysFromSample } from '../view/ConfigTools.js';

/**
 * Виджет «jQuery UI Tabs-подобных» вкладок без использования jQuery.
 *
 * Поддерживает разметку вида:
 * ```html
 * <ul>
 *   <li><a href="#id1">...</a></li>
 *   <li><a href="#id2">...</a></li>
 * </ul>
 * <div id="id1">...</div>
 * <div id="id2">...</div>
 * ```
 *
 *  * Допустимо с обёрткой:
 * <div class="im-jqtabs">
 *   <div class="im-jqtabs-nav">
 *     <ul>...</ul>
 *   </div>
 *   ...
 * </div>
 */
class TabsWidget extends Widget {
    constructor() {
        super(...arguments);
        /** Ссылки-якоря на вкладки */
        this._anchors = [];
        /** Панели содержимого, связанные с якорями */
        this._tabsPanels = [];
        /** Индекс активной вкладки */
        this._activeIndex = -1;
        /** Список отписчиков для корректной очистки */
        this._unbinders = [];
    }
    /** Ключевой класс для поиска виджета */
    static _cssKey() {
        return 'im-jqtabs';
    }
    /** Схема названий полей опций (для приведения регистра) */
    static _optionScheme() {
        return keysFromSample({
            currentTab: 0,
            urlParam: 0,
            urlDefaultTab: 0
        });
    }
    /** Текущий активный индекс (совместимость с API старого TabsWidget) */
    get activeIndex() { return this._activeIndex; }
    /**
     * Находит индекс вкладки по id панели.
     * Поддерживает boolean/string и сравнение без учета регистра.
     */
    _findTabIndexById(value) {
        if (value == null)
            return -1;
        const needleRaw = String(value).trim();
        if (!needleRaw)
            return -1;
        // 1) точное совпадение
        let idx = this._anchors.findIndex(a => ((a.dataset.href || a.getAttribute('href') || '').slice(1) === needleRaw));
        if (idx >= 0)
            return idx;
        // 2) fallback: без учета регистра
        const needle = needleRaw.toLowerCase();
        idx = this._anchors.findIndex(a => ((a.dataset.href || a.getAttribute('href') || '').slice(1).toLowerCase() === needle));
        return idx;
    }
    /** Инициализация: находит вкладки и панели, вешает события */
    _create(op) {
        this._urlParam = op.urlParam;
        this._urlDefaultTab = op.urlDefaultTab;
        console.log('urlparam', this._urlParam, "defaulttab", this._urlDefaultTab);
        // Ищем вкладки
        const tablist = this.element.querySelector(':scope .im-jqtabs-nav ul')
            ?? this.element.querySelector(':scope > ul');
        this._anchors = [];
        if (tablist) {
            // 1) Собираем все якоря вида <li><a href="#..."></a></li>
            this._anchors = Array.from(tablist.querySelectorAll(':scope > li > a[href^="#"]')).filter(a => {
                const href = a.dataset.href ?? a.getAttribute('href') ?? '';
                const id = href.startsWith('#') ? href.slice(1) : '';
                return !!id; // НЕ фильтруем по существованию панели — она может быть вложена/подъехать позже
            });
            // 2) Ищем панели строго по id из якорей во всём поддереве виджета
            const ids = new Set(this._anchors
                .map(a => (a.dataset.href ?? a.getAttribute('href') ?? ''))
                .map(h => h.startsWith('#') ? h.slice(1) : '')
                .filter(Boolean));
            this._tabsPanels = Array.from(ids)
                .map(id => this.element.querySelector(`#${id}`))
                .filter((p) => !!p);
        }
        // контейнер — jQuery UI классы
        this.element.classList.add('ui-tabs', 'ui-widget', 'ui-widget-content', 'ui-corner-all');
        // заголовки (ul)
        if (tablist) {
            tablist.classList.add('ui-tabs-nav', 'ui-helper-reset', 'ui-helper-clearfix', 'ui-widget-header', 'ui-corner-all');
            tablist.setAttribute('role', 'tablist');
        }
        for (const a of this._anchors) {
            a.classList.add('ui-tabs-anchor');
            a.setAttribute('role', 'tab');
            const li = a.parentElement;
            li?.classList.add('ui-state-default', 'ui-corner-top', 'ui-tabs-tab', 'ui-tab');
            if (!a.id)
                a.id = 'ui-id-' + Math.random().toString(36).slice(2);
        }
        // панели
        const ids = new Set(this._anchors.map(a => ((a.dataset.href || a.getAttribute("href") || "").slice(1))).filter(Boolean));
        this._tabsPanels = Array.from(this.element.querySelectorAll('div[id]'))
            .filter(d => ids.has(d.id));
        for (const p of this._tabsPanels) {
            p.classList.add('ui-tabs-panel', 'ui-widget-content', 'ui-corner-bottom');
            p.setAttribute('role', 'tabpanel');
            p.setAttribute('tabindex', '-1');
            // захардкодим связь aria-controls/labelledby позже в _reflect (на активной), но базово дадим id
            // (ничего не делаем здесь)
        }
        const isAutoswitch = this.element.classList.contains('autoswitch');
        // Клик по вкладке
        const onClick = (e) => {
            const a = e.target.closest('a[href^="#"]');
            if (!a)
                return;
            // только клики по якорям из tablist
            if (!tablist || !tablist.contains(a))
                return;
            // только «наши» вкладочные якоря
            if (!this._anchors.includes(a))
                return;
            // только валидные href (#id), а не "#"
            const targetId = (a.dataset.href || a.getAttribute('href') || '').slice(1);
            if (!targetId)
                return;
            e.preventDefault();
            const _idx = this._anchors.indexOf(a);
            if (_idx >= 0)
                this._selectTab(_idx, /*focus*/ true, /*emit*/ true);
        };
        this.element.addEventListener('click', onClick);
        this._unbinders.push(() => this.element.removeEventListener('click', onClick));
        // Переключение по наведению (autoswitch)
        if (isAutoswitch) {
            const onOver = (e) => {
                const a = e.target.closest('a[href^="#"]');
                if (!a || !this._anchors.includes(a))
                    return;
                const _idx = this._anchors.indexOf(a);
                if (_idx >= 0)
                    this._selectTab(_idx, /*focus*/ false, /*emit*/ true);
            };
            this.element.addEventListener('mouseover', onOver);
            this._unbinders.push(() => this.element.removeEventListener('mouseover', onOver));
        }
        // Клавиатура: реагируем в tablist ИЛИ внутри панели, но не в редактируемых полях
        const onKeydown = (e) => {
            const target = e.target;
            if (!target)
                return;
            // 0) игнор, если зажаты модификаторы
            if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey)
                return;
            // 1) если фокус в редактируемом — не трогаем
            const isEditable = (el) => {
                if (el.isContentEditable)
                    return true;
                const tag = el.tagName;
                if (tag === 'TEXTAREA') {
                    const t = el;
                    return !t.readOnly && !t.disabled;
                }
                if (tag === 'INPUT') {
                    const i = el;
                    const textLike = !['button', 'submit', 'reset', 'checkbox', 'radio', 'range', 'color', 'file', 'image'].includes(i.type);
                    return textLike && !i.readOnly && !i.disabled;
                }
                if (tag === 'SELECT') {
                    const s = el;
                    return !s.disabled;
                }
                if (el.getAttribute('role') === 'textbox')
                    return true;
                if (el.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="textbox"]'))
                    return true;
                return false;
            };
            if (isEditable(target))
                return;
            // 2) реагируем, если фокус в tablist ИЛИ внутри панели
            const inTablist = !!target.closest('[role="tablist"]');
            const inPanel = !!target.closest('.ui-tabs-panel, [role="tabpanel"]');
            if (!inTablist && !inPanel)
                return;
            const current = this._activeIndex;
            if (current < 0)
                return;
            switch (e.key) {
                case 'ArrowLeft':
                case 'Left':
                    e.preventDefault();
                    this._selectTab((current - 1 + this._anchors.length) % this._anchors.length, true);
                    break;
                case 'ArrowRight':
                case 'Right':
                    e.preventDefault();
                    this._selectTab((current + 1) % this._anchors.length, true);
                    break;
                case 'Home':
                    e.preventDefault();
                    this._selectTab(0, true);
                    break;
                case 'End':
                    e.preventDefault();
                    this._selectTab(this._anchors.length - 1, true);
                    break;
            }
        };
        this.element.addEventListener('keydown', onKeydown);
        this._unbinders.push(() => this.element.removeEventListener('keydown', onKeydown));
        // если нужно — читаем стартовую вкладку из URL
        if (this._urlParam) {
            const fromUrl = new URLSearchParams(window.location.search)
                .get(this._urlParam);
            if (fromUrl) {
                const idx = this._findTabIndexById(fromUrl);
                if (idx >= 0) {
                    this._selectTab(idx, /*focus*/ false, /*emit*/ false);
                    return;
                }
            }
        }
        // Стартовая панель
        if (op.currentTab !== undefined && op.currentTab !== null) {
            const idx = this._findTabIndexById(op.currentTab);
            if (idx >= 0) {
                this._selectTab(idx, /*focus*/ false, /*emit*/ false);
                return;
            }
        }
        this._selectTab(0, /*focus*/ false, /*emit*/ false);
    }
    /**
     * Активирует вкладку по индексу или по id панели, с взаимным fallback.
     *
     * Поведение:
     * - Если передано число: пытаемся индекс; если индекс вне диапазона — пробуем как id (строка).
     * - Если передана строка: пытаемся id панели; если не нашли — пробуем распарсить как число (индекс).
     *
     * @param target Индекс вкладки (0..N-1) или id панели содержимого
     * @param focus  Ставить ли фокус на активный якорь (по умолчанию true)
     */
    setActive(target, focus = true) {
        if (this._anchors.length === 0)
            return;
        // local helpers
        const tryIndex = (idx) => {
            if (!Number.isFinite(idx))
                return false;
            if (idx < 0 || idx >= this._anchors.length)
                return false;
            this._selectTab(idx, /*focus*/ focus);
            return true;
        };
        const tryId = (id) => {
            if (!id)
                return false;
            const i = this._findTabIndexById(id);
            if (i < 0)
                return false;
            this._selectTab(i, /*focus*/ focus);
            return true;
        };
        // main logic with cross-fallback
        if (typeof target === "number") {
            if (!tryIndex(target)) {
                // fallback: трактуем как строковый id
                tryId(String(target));
            }
        }
        else {
            if (!tryId(target)) {
                // fallback: если строка выглядит как число — пробуем индекс
                const asNum = Number(target);
                if (Number.isFinite(asNum))
                    tryIndex(asNum);
            }
        }
    }
    /**
     * Внутренний метод переключения вкладки.
     * @param index индекс вкладки
     * @param focusAnchor установить ли фокус на активный якорь
     * @param emit генерировать ли событие tabsactivate/change (по умолчанию true)
     */
    _selectTab(index, focusAnchor, emit = true) {
        const clamped = Math.max(0, Math.min(index, this._anchors.length - 1));
        if (this._activeIndex === clamped) {
            this._reflect(clamped, focusAnchor, /*emit*/ false);
            return;
        }
        const prev = this._activeIndex;
        this._activeIndex = clamped;
        this._reflect(clamped, focusAnchor, emit, prev);
    }
    /**
     * Обновляет DOM для выбранной вкладки.
     * @param index индекс новой вкладки
     * @param focusAnchor ставить ли фокус на якорь
     * @param emit генерировать ли событие tabsactivate
     * @param prevIndex предыдущий активный индекс
     */
    _reflect(index, focusAnchor, emit, prevIndex = -1) {
        const href = this._anchors[index]?.dataset.href || this._anchors[index]?.getAttribute('href') || '';
        const targetId = href.startsWith('#') ? href.slice(1) : '';
        // если по какой-то причине не нашли id — не скрываем всё, а тихо выходим
        if (!targetId) {
            // восстановим корректные aria для текущего состояния, но без тотального hide
            if (focusAnchor)
                this._anchors[index]?.focus();
            return;
        }
        let newPanel = null;
        let oldPanel = null;
        // скрыть/показать панели: и hidden, и ui-tabs-hide
        for (const p of this._tabsPanels) {
            const isActive = p.id === targetId;
            if (isActive) {
                p.removeAttribute('hidden');
                p.classList.remove('ui-tabs-hide');
                p.setAttribute('aria-hidden', 'false');
                p.setAttribute('tabindex', '0'); // ✅ активная панель доступна
                // Снять inline display:none, если был
                DomHelpers.show(p);
                newPanel = p;
            }
            else {
                DomHelpers.hide(p);
                p.classList.add('ui-tabs-hide');
                p.setAttribute('aria-hidden', 'true');
                p.setAttribute('tabindex', '-1'); // ✅ неактивные исключаем из фокуса
            }
        }
        // подсветка активного таба
        for (const a of this._anchors) {
            a.setAttribute('aria-selected', 'false');
            a.setAttribute('tabindex', '-1');
            a.parentElement?.classList.remove('ui-tabs-active', 'ui-state-active');
        }
        const activeAnchor = this._anchors[index];
        if (activeAnchor) {
            activeAnchor.setAttribute('aria-selected', 'true');
            activeAnchor.setAttribute('tabindex', '0');
            activeAnchor.parentElement?.classList.add('ui-tabs-active', 'ui-state-active');
        }
        if (focusAnchor)
            activeAnchor?.focus();
        // ARIA связи: tab ↔ panel
        if (activeAnchor && newPanel) {
            activeAnchor.setAttribute('aria-controls', newPanel.id);
            newPanel.setAttribute('aria-labelledby', activeAnchor.id);
        }
        if (prevIndex >= 0) {
            const prevA = this._anchors[prevIndex];
            const prevId = ((prevA?.dataset.href || prevA?.getAttribute('href') || '')).slice(1);
            oldPanel = this._tabsPanels.find(p => p.id === prevId) || null;
        }
        if (newPanel)
            newPanel.setAttribute('tabindex', '0');
        if (oldPanel)
            oldPanel.setAttribute('tabindex', '-1');
        if (emit)
            this._emitTabsActivate(newPanel, oldPanel);
    }
    /**
     * Генерация событий при смене вкладки.
     * @param newPanel новая активная панель
     * @param oldPanel старая панель
     */
    _emitTabsActivate(newPanel, oldPanel) {
        // синхронизация URL без записи в историю
        if (this._urlParam) {
            const url = new URL(window.location.href);
            if (newPanel?.id && this._urlDefaultTab && newPanel.id === this._urlDefaultTab) {
                // дефолтная вкладка → параметр убираем
                url.searchParams.delete(this._urlParam);
            }
            else if (newPanel?.id) {
                // обычная вкладка → пишем id
                url.searchParams.set(this._urlParam, newPanel.id);
            }
            history.replaceState(null, '', url.toString());
        }
        // Всегда диспатчим tabsactivate
        const evt = new CustomEvent('tabsactivate', { detail: { newPanel, oldPanel } });
        this.element.dispatchEvent(evt);
        // Дополнительно диспатчим change, если у контейнера есть data-key
        if (this.element.dataset.key) {
            this.element.dispatchEvent(new Event("change", { bubbles: false }));
        }
    }
    /**
     * Вернуть «основное значение» выбранной вкладки.
     * Работает только если сам контейнер TabsWidget имеет data-key —
     * тогда encodeValues использует его как ключ.
     * Значением будет id активной панели.
     */
    getValue() {
        if (!this.element.dataset.key)
            return undefined;
        const idx = this._activeIndex;
        if (idx < 0 || idx >= this._tabsPanels.length)
            return undefined;
        // возвращаем id активной панели
        return this._tabsPanels[idx].id || undefined;
    }
    /**
     * Установить «основное значение» выбранной вкладки.
     *
     * Принимает только строку и активирует вкладку по совпадению с `id` панели.
     *
     * Семантика возврата: ВСЕГДА `true` — попытка установки обработана,
     * дальнейшие обработчики вызывать не требуется.
     *
     * @param val id панели. null/undefined игнорируются.
     * @returns true — значение обработано (независимо от фактического переключения)
     */
    setValue(val) {
        // null/undefined — считаем обработанным, ничего не делаем
        if (val == null)
            return true;
        // нет вкладок — тоже обработано
        if (this._anchors.length === 0)
            return true;
        // 2) пробуем по id панели
        let i = this._tabsPanels.findIndex(p => p.id === val);
        if (i >= 0) {
            this._selectTab(i, /*focus*/ true);
            return true;
        }
        // ничего не переключили, но попытку обработали
        return true;
    }
    /** true — если у контейнера есть data-key и активная панель имеет непустой id */
    hasValue() {
        if (!this.element.dataset.key)
            return false;
        const idx = this._activeIndex;
        if (idx < 0 || idx >= this._tabsPanels.length)
            return false;
        return !!this._tabsPanels[idx].id;
    }
    /** Очистка ресурсов и отписка от событий */
    _onDestroy() {
        for (const u of this._unbinders)
            u();
        this._unbinders = [];
        this._anchors = [];
        this._tabsPanels = [];
    }
}
/** Имя виджета */
TabsWidget._alias = "Tabs";

export { TabsWidget };
